// Date helpers. All functions operate in the user's local timezone.
// Recurrence math on the backend is UTC; what we display in views is local.

const DAY_MS = 24 * 60 * 60 * 1000;

export function startOfLocalDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

export function endOfLocalDay(d: Date): Date {
  return new Date(startOfLocalDay(d).getTime() + DAY_MS);
}

export function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

export function isSameLocalDay(a: Date, b: Date): boolean {
  return startOfLocalDay(a).getTime() === startOfLocalDay(b).getTime();
}

// Monday-first week start. Monday = 1, Sunday = 0 in JS getDay(); we adjust
// so a Monday returns itself, a Sunday returns the previous Monday.
export function startOfWeek(d: Date): Date {
  const start = startOfLocalDay(d);
  const dow = start.getDay();
  const offset = dow === 0 ? -6 : 1 - dow;
  return addDays(start, offset);
}

// ISO 8601 week number (1–53). Monday-first weeks; week 1 is the one
// containing the year's first Thursday. We shift to the Thursday of
// the date's ISO week so years resolve correctly around Jan 1 (a
// late-December date can legitimately belong to week 52/53 of the
// previous year, or vice versa).
//
// Day-count is done in UTC so DST transitions don't shave/add an hour
// to the local-millisecond delta and round us into the wrong bucket.
export function isoWeekNumber(d: Date): number {
  const base = startOfLocalDay(d);
  const dow = (base.getDay() + 6) % 7; // Mon=0..Sun=6
  const thursday = addDays(base, 3 - dow);
  const utcThursday = Date.UTC(
    thursday.getFullYear(),
    thursday.getMonth(),
    thursday.getDate(),
  );
  const utcYearStart = Date.UTC(thursday.getFullYear(), 0, 1);
  const diffDays = Math.round((utcThursday - utcYearStart) / DAY_MS);
  return Math.floor(diffDays / 7) + 1;
}

// Long format used in headers: "Saturday, May 9".
export function formatDayLong(d: Date): string {
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

// Short format used in week-view column headers: "Sat 9".
export function formatDayShort(d: Date): string {
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' });
}

// Format a Date as the "yyyy-MM-ddThh:mm" string an <input type="datetime-local">
// expects (in local time, not UTC).
export function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}`;
}

// Inverse of toDatetimeLocalValue: take the input value and turn it into
// a real Date (interpreted in the local zone).
export function fromDatetimeLocalValue(value: string): Date {
  return new Date(value);
}

// Minutes since midnight, used to position blocks on the day timeline.
export function minutesSinceMidnight(d: Date): number {
  return d.getHours() * 60 + d.getMinutes();
}
